import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subscription, filter, map, mergeMap } from 'rxjs';
import { AppSettings } from 'src/app/app.config';
import { Profile } from 'src/app/models/profile.model';
import { CoreService } from 'src/app/services/core.service';
import { MenuState } from 'src/app/store-ngxs/states/menu.state';
import { OptionsState } from 'src/app/store-ngxs/states/options.state';
import { ProfileState } from 'src/app/store-ngxs/states/profile.state';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

@Component({
  selector: 'app-custom',
  templateUrl: './custom.component.html',
  styleUrls: ['./custom.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomComponent implements OnInit, OnDestroy {
  mainNavigation: any[] = [
    {
      header: 'Home',
    },
    {
      name: 'Dashboard',
      icon: 'box-multiple',
      route: '/',
    },
    {
      header: 'App',
    },
  ];

  profile: Profile;

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;

  @ViewChild('content', { static: true }) content!: MatSidenavContent;

  options = this.settings.getOptions();
  private optionsSubscription: Subscription;
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  hideSidebar = false;

  private routerSubscription: Subscription;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  constructor(
    private settings: CoreService,
    private breakpointObserver: BreakpointObserver,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.htmlElement = document.querySelector('html')!;

    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        this.hideSidebar = data['hideSidebar'] || false;
      });

    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });
    this.checkInitialRouteForSidebar();

    this.receiveOptions(this.options);
  }

  ngOnInit(): void {
    this.store.select(MenuState.menu).subscribe({
      next: (menu) => {
        this.mainNavigation = menu;
      },
      error: (error) => {
        console.error('Error fetching menu:', error);
      },
    });

    this.store.select(ProfileState.profile).subscribe({
      next: (response) => {
        this.profile = response as Profile;
      },
      error: (error) => {
        console.error('Error fetching profile:', error);
      },
    });

    this.optionsSubscription = this.store
      .select(OptionsState.options)
      .subscribe((options) => {
        this.options = { ...this.options, ...options };
        this.toggleDarkTheme(this.options);
      });
  }

  ngOnDestroy(): void {
    this.layoutChangesSubscription.unsubscribe();
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  checkInitialRouteForSidebar() {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    this.hideSidebar = currentRoute.snapshot?.data?.['hideSidebar'] || false;
  }
}
